import * as _ from 'underscore'
import { defaultFoldingExcludeList, XhprofCompactGraphCallExcludeList } from './XhprofCompactGraphFoldingExcludeList'
import { XhprofFlatInfoData } from './XhprofReportGenerator'

export class XhprofGraphCompactor {
    private foldingExcludeList: XhprofCompactGraphCallExcludeList

    constructor(foldingExcludeList: XhprofCompactGraphCallExcludeList = defaultFoldingExcludeList) {
        this.foldingExcludeList = foldingExcludeList
    }

    public compactGraph = (
        graph: XhprofFlatInfoData,
        threshold: number,
        dontFoldExcludeList: XhprofCompactGraphCallExcludeList = {},
    ): XhprofFlatInfoData => {
        const graphCopy: XhprofFlatInfoData = {}

        // copy all nodes
        Object.keys(graph).forEach(call => {
            graphCopy[call] = {
                ...graph[call],
                parents: { ...graph[call].parents },
                children: { ...graph[call].children },
            }
        })

        this.findChains(graphCopy, threshold, { ...this.foldingExcludeList, ...dontFoldExcludeList }).forEach(chain =>
            this.collapseChain(graphCopy, chain),
        )

        return graphCopy
    }

    private findChains = (graph: XhprofFlatInfoData, threshold: number, dontFoldExcludeList: XhprofCompactGraphCallExcludeList) => {
        if (threshold < 50) {
            throw Error('threshold <50% does not make sense, more than one child could match this')
        }

        const edges: Edge[] = []
        Object.keys(graph).forEach(call => {
            // our root should not be aggregated
            if (call === 'main()') {
                return
            }

            const currentThreshold = Math.abs((threshold / 100) * graph[call].wt)
            const callWalltime = Math.abs(graph[call].wt)

            let foundChild = false

            Object.keys(graph[call].children).forEach(child => {
                if (foundChild || child in dontFoldExcludeList) {
                    return
                }

                // recursive calls should not be collapsed
                if (child === call) {
                    return
                }

                // leaves should never be collapsed
                if (Object.keys(graph[child].children).length === 0) {
                    return
                }

                const edgeWalltime = Math.abs(graph[call].children[child].wt)
                let { wt: childTotaltime, excl_wt: childSelftime } = graph[child]
                childTotaltime = Math.abs(childTotaltime)
                childSelftime = Math.abs(childSelftime)

                if (childTotaltime < currentThreshold || edgeWalltime < currentThreshold) {
                    return
                }

                // chain leading up to large node
                if (childTotaltime > callWalltime) {
                    return
                }

                // if a node spends almost all of its time by itself, it's
                // probably highly computational or an extension calling c-functions etc
                // and therefore important -> don't chain
                if (childSelftime / childTotaltime > 0.9) {
                    return
                }

                edges.push({ from: call, to: child })

                // skip searching other children
                foundChild = true
            })
        })

        const chains: Chain[] = []
        const visited = {}
        edges.forEach((edge, index) => {
            if (index in visited) {
                return
            }

            let currentChain: Chain = { nodeIds: [edge.from, edge.to] }

            let current = edge

            // make sure our target node is not yet reserved for another chain
            if (edges.map(e => e.to).filter(node => node === current.to).length > 1) {
                return
            }

            // go up as long as possible
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const indexParent = edges.map(e => e.to).indexOf(current.from)

                if (indexParent === -1) {
                    break
                }

                visited[indexParent] = true
                currentChain = { nodeIds: [edges[indexParent].from, ...currentChain.nodeIds] }

                current = edges[indexParent]
            }

            current = edge
            // go down as long as possible
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const indexChild = edges.map(e => e.from).indexOf(current.to)

                if (indexChild === -1) {
                    break
                }

                visited[indexChild] = true

                currentChain = { nodeIds: [...currentChain.nodeIds, edges[indexChild].to] }

                current = edges[indexChild]
            }

            chains.push(currentChain)
        })
        return chains
    }

    private collapseChain = (graph: XhprofFlatInfoData, chain: Chain): void => {
        const { nodeIds } = chain

        if (nodeIds.length <= 2) {
            return
        }

        const [first, ...rest] = nodeIds
        const last = rest[rest.length - 1]

        graph[first] = {
            ...graph[first],
            excl_wt: nodeIds.reduce((sumSelfTime, node) => sumSelfTime + graph[node].excl_wt, 0),
            children: { ...graph[last].children },
            foldedNodes: [...rest],
        }

        rest.forEach(collapsedCall => {
            // omit child connection in parents
            Object.keys(graph[collapsedCall].parents).forEach(parent => {
                if (nodeIds.indexOf(parent) !== -1) {
                    return
                }

                graph[parent].children = _.omit(graph[parent].children, collapsedCall)
            })

            // replace parent connection in children with connection to first
            Object.keys(graph[collapsedCall].children).forEach(child => {
                if (nodeIds.indexOf(child) !== -1) {
                    return
                }

                const { [collapsedCall]: collapsedParent, ...parents } = graph[child].parents

                graph[child].parents = { ...parents, [first]: { ...collapsedParent, name: first } }
            })
        })

        rest.forEach(nodeId => delete graph[nodeId])
    }
}

interface Chain {
    nodeIds: string[]
}

interface Edge {
    from: string
    to: string
}

export const xhprofCompactGraph = (
    graph: XhprofFlatInfoData,
    threshold: number,
    dontFoldExcludeList: XhprofCompactGraphCallExcludeList = {},
): XhprofFlatInfoData => {
    const compactor = new XhprofGraphCompactor()

    return compactor.compactGraph(graph, threshold, dontFoldExcludeList)
}
